import * as Sentry from "@sentry/react";

// Sentry runs in prod/staging, or locally with an explicit escape hatch. One
// allowlist for every browser app so the gate can't drift between them.
const DEPLOYED_SENTRY_ENVS = new Set(["prod", "staging"]);

/**
 * Clamp a `VITE_*`-sourced sample rate to [0, 1], falling back on absent /
 * malformed / out-of-range input.
 */
export function parseSampleRate(value: string | undefined, fallback: number) {
  if (!value) {
    return fallback;
  }
  const parsed = Number(value);
  if (Number.isNaN(parsed) || parsed < 0 || parsed > 1) {
    return fallback;
  }
  return parsed;
}

// Derived from Sentry.init's own parameter so we never have to name the
// `Integration` type, which @sentry/react does not re-export by that name.
type BrowserIntegrations = NonNullable<
  NonNullable<Parameters<typeof Sentry.init>[0]>["integrations"]
>;

export type InitBrowserSentryOptions = {
  /** Global-scope `service` tag, e.g. "web" or "control-plane-web". */
  service: string;
  dsn: string | undefined;
  environment: string | undefined;
  stackName: string | undefined;
  /** `import.meta.env.MODE` from the calling app. */
  mode: string | undefined;
  /** `import.meta.env.VITE_SENTRY_FORCE_LOCAL === "1"` from the calling app. */
  forceLocal: boolean;
  release: string | undefined;
  /** App-specific integrations (router variant, replay, …). */
  integrations: BrowserIntegrations;
  tracePropagationTargets: (string | RegExp)[];
  tracesSampleRate: number;
  replaysSessionSampleRate: number;
  replaysOnErrorSampleRate: number;
};

/**
 * Initialize browser Sentry with the shared gate, init shape and global-scope
 * tagging. Callers supply only what genuinely differs between apps — the
 * `service`, the DSN/env resolved from runtime config, and the integration and
 * trace-propagation lists. No-op unless a DSN is present, the env is deployed
 * (or `forceLocal`), and this is not a test build.
 */
export function initBrowserSentry(options: InitBrowserSentryOptions) {
  const sentryEnv = options.environment ?? options.mode;
  const allowed = options.forceLocal || DEPLOYED_SENTRY_ENVS.has(sentryEnv ?? "");
  if (!options.dsn || options.mode === "test" || !allowed) {
    return;
  }

  Sentry.init({
    dsn: options.dsn,
    environment: options.environment ?? options.mode,
    release: options.release,
    sendDefaultPii: true,
    integrations: options.integrations,
    tracesSampleRate: options.tracesSampleRate,
    tracePropagationTargets: options.tracePropagationTargets,
    replaysSessionSampleRate: options.replaysSessionSampleRate,
    replaysOnErrorSampleRate: options.replaysOnErrorSampleRate,
    enableLogs: true,
  });

  const stack = options.stackName ?? options.environment ?? options.mode ?? "unknown";
  Sentry.getGlobalScope().setAttributes({
    service: options.service,
    runtime: "browser",
    stack,
  });
  Sentry.getGlobalScope().setTag("stack", stack);
}
